import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { ContentService } from '../../services/content.service';
import { TravelerManagement } from 'src/app/services/dataLayer/CAM/traveler-management.service';
import { CamService } from 'src/app/services/camService/cam.service';
import { jwtDecode } from 'jwt-decode';
import { ToastrApiService } from 'src/app/shared/common/toastr-api.service';
import { CommonService } from 'src/app/shared/common/common.service';
import { NgIf, NgFor, DatePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { ExpiryDataCalendarComponent } from '../../../passenger/components/expiry-data-calendar/expiry-data-calendar.component';
import { MatButton, MatIconButton } from '@angular/material/button';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger
} from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/core';

@Component({
    selector: 'app-delegation',
    templateUrl: './delegation.component.html',
    styleUrls: ['./delegation.component.scss'],
    imports: [ReactiveFormsModule, NgIf, MatIcon, MatSlideToggle, MatFormField, MatLabel, MatSuffix, MatInput, ExpiryDataCalendarComponent, NgFor, MatButton, MatIconButton, MatAutocomplete, MatAutocompleteTrigger, MatOption, DatePipe]
})
export class DelegationComponent implements OnInit, OnDestroy {
  @ViewChild('leaveStartCalendar', { read: ElementRef }) leaveStartRef!: ElementRef;
  @ViewChild('leaveEndCalendar', { read: ElementRef }) leaveEndRef!: ElementRef;
  private destroy$ = new Subject<void>();
  content: any;

  delegationForm!: FormGroup;

  delegatedList: any[] = [];
  private initialDelegatedList: any[] = [];
  delegatedSearchControl = new FormControl('');
  delegatedNoItemsText: string = 'Search with name';
  private delegatedSearchTimer: ReturnType<typeof setTimeout> | null = null;

  selectedLeaveStart: Date | null = null;
  selectedLeaveEnd: Date | null = null;

  hasDelegationData: boolean = false;
  Loader: any = {
    isEditableClicked: true
  };
  private initialDelegationSnapshot: any = null;

  /** Snapshot when user clicks Edit; Save stays disabled until current state differs. */
  private editSessionBaseline: {
    onLeave: boolean;
    delegatedId: string | null;
    startKey: string | null;
    endKey: string | null;
  } | null = null;

  isCalenderVisible: { [key: string]: boolean } = { leaveStart: false, leaveEnd: false };
  commonObj: any = {
    minDate: this.formatDate(new Date())
  };

  constructor(
    private readonly fb: FormBuilder,
    private readonly contentService: ContentService,
    private readonly travelerDataLayer: TravelerManagement,
    private readonly camService: CamService,
    private readonly toastr: ToastrApiService,
    private readonly commonService: CommonService, private _contentService: ContentService
  ) {
    this.delegationForm = this.fb.group({
      onLeave: [false],
      leaveStart: [''],
      leaveEnd: [''],
      delegatedTo: [null]
    });
  }

  ngOnInit() {
    this.contentService.content$
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.content = res;
      });

    this.delegationForm
      .get('onLeave')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((onLeave) => this.updateDelegationValidators(!!onLeave));

    this.delegationForm
      .get('delegatedTo')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((delegatedTo) => {
        if (delegatedTo === null || delegatedTo === undefined || delegatedTo === '') {
          this.delegatedList = [];
        }
      });

    this.updateDelegationValidators(!!this.delegationForm.get('onLeave')?.value);

    // Default view mode: buttons hidden, fields disabled
    this.delegationForm.disable();
    this.syncDelegatedFieldState();
    this.loadDelegationDetails();
      this._contentService.content$.subscribe((res: any) => {
                  if (res) {
                      this.ManageTravelerDataData = res;
                  }
              });
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    const target = event.target as HTMLElement;
  
    const clickedInsideStart = this.leaveStartRef?.nativeElement?.contains(target);
    const clickedInsideEnd = this.leaveEndRef?.nativeElement?.contains(target);
  
    if (!clickedInsideStart && !clickedInsideEnd) {
      this.isCalenderVisible['leaveStart'] = false;
      this.isCalenderVisible['leaveEnd'] = false;
    }
  }

  private updateDelegationValidators(onLeave: boolean): void {
    const leaveStart = this.delegationForm.get('leaveStart');
    const leaveEnd = this.delegationForm.get('leaveEnd');
    const delegatedTo = this.delegationForm.get('delegatedTo');

    if (onLeave) {
      leaveStart?.setValidators([Validators.required]);
      leaveEnd?.setValidators([Validators.required]);
      delegatedTo?.setValidators([Validators.required]);
    } else {
      leaveStart?.clearValidators();
      leaveEnd?.clearValidators();
      delegatedTo?.clearValidators();
    }

    leaveStart?.updateValueAndValidity({ emitEvent: false });
    leaveEnd?.updateValueAndValidity({ emitEvent: false });
    delegatedTo?.updateValueAndValidity({ emitEvent: false });
    this.delegationForm.updateValueAndValidity({ emitEvent: false });
  }

  /** Save disable rules based on toggle state. */
  get saveButtonDisabled(): boolean {
    if (this.Loader.isEditableClicked) {
      return true;
    }
  
    const onLeave = !!this.delegationForm.get('onLeave')?.value;
    return onLeave && this.delegationForm.invalid;
  }

  private captureEditBaseline(): {
    onLeave: boolean;
    delegatedId: string | null;
    startKey: string | null;
    endKey: string | null;
  } {
    return {
      onLeave: !!this.delegationForm.get('onLeave')?.value,
      delegatedId: this.normalizeDelegatedId(this.delegationForm.get('delegatedTo')?.value),
      startKey: this.dateToDayKey(this.selectedLeaveStart),
      endKey: this.dateToDayKey(this.selectedLeaveEnd)
    };
  }

  private normalizeDelegatedId(v: unknown): string | null {
    if (v === null || v === undefined || v === '') {
      return null;
    }
    return String(v);
  }

  private dateToDayKey(d: Date | null): string | null {
    if (!d || isNaN(d.getTime())) {
      return null;
    }
    const iso = this.toISOMidnight(d);
    return iso ? iso.split('T')[0]! : null;
  }

  private isDelegationChangedFromEdit(): boolean {
    if (!this.editSessionBaseline) {
      return false;
    }
    const cur = this.captureEditBaseline();
    const b = this.editSessionBaseline;
    return (
      b.onLeave !== cur.onLeave ||
      b.delegatedId !== cur.delegatedId ||
      b.startKey !== cur.startKey ||
      b.endKey !== cur.endKey
    );
  }

  private getDelegatedIdentityKey(user: any): string {
    const userName = String(user?.userName ?? '').trim().toLowerCase();
    const emailAddress = String(user?.emailAddress ?? '').trim().toLowerCase();
    const employeeCode = String(user?.employeeCode ?? '').trim().toLowerCase();
    return `${userName}|${emailAddress}|${employeeCode}`;
  }

  private getUniqueDelegatedList(users: any[]): any[] {
    const seen = new Set<string>();
    return (users ?? []).filter((user) => {
      const key = this.getDelegatedIdentityKey(user);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  onFocusDelegatedTo(): void {
    const value = this.delegationForm.get('delegatedTo')?.value;

    if (value === null || value === undefined || value === '') {
      this.delegatedNoItemsText = 'Search with name';
      this.delegatedList = [];
    }
  }

  onDelegatedSearchInput(event: Event): void {
    const term = (event.target as HTMLInputElement).value.trim();

    if (term === '') {
      this.delegationForm.get('delegatedTo')?.setValue(null);
    }

    this.onDelegatedSearch(term);
  }

  onDelegatedSelected(event: MatAutocompleteSelectedEvent): void {
    const user = event.option.value;
    this.delegationForm.get('delegatedTo')?.setValue(user?.b2BUserId ?? null);
    this.delegatedSearchControl.setValue(user?.userName ?? '', { emitEvent: false });
  }

  clearDelegatedTo(event: Event): void {
    event.stopPropagation();
    this.delegationForm.get('delegatedTo')?.setValue(null);
    this.delegatedSearchControl.setValue('');
    this.delegatedList = [];
    this.delegatedNoItemsText = 'Search with name';
  }

  onDelegatedSearch(term: string): void {
    const value = this.delegationForm.get('delegatedTo')?.value;

    if((term === '' || term.length === 0) && !value) {
      this.delegatedNoItemsText = 'Search with name';
      this.delegatedList = [];
      return;
    }

    if (term.length < 3 && !value) {
      this.delegatedNoItemsText = 'Search with name';
      this.delegatedList = [];
      if (this.delegatedSearchTimer) {
        clearTimeout(this.delegatedSearchTimer);
        this.delegatedSearchTimer = null;
      }
      return;
    }

    this.delegatedNoItemsText = this.ManageTravelerDataData?.['b2b.managetraveler.noitemsfound'] || this.ManageTravelerDataData?.b2b.managetraveler.noitemsfound || 'No items found';

    // 3+ letters: hit API on each change (debounced)
    if (this.delegatedSearchTimer) {
      clearTimeout(this.delegatedSearchTimer);
    }

    this.delegatedSearchTimer = setTimeout(() => {
      this.fetchDelegatedList(term);
    }, 250);
  }

  private async fetchDelegatedList(searchParam: string): Promise<void> {
    try {
      const user = this.getUserFromToken();
      if (!user) {
        this.delegatedList = [];
        return;
      }

      const payload = {
        subagentId: user.subAgentId,
        userRole: user.role,
        b2BUserId: user.userId
      };

      const res: any = await this.camService.getManagerList(
        user.subAgentId,
        user.uid,
        searchParam,
        payload
      );

      this.delegatedList = this.getUniqueDelegatedList(res?.data?.managers ?? []);
      if (this.delegatedList.length === 0) {
        this.delegationForm.get('delegatedTo')?.setValue(null);
      }

    } catch (error) {
      console.error('Error fetching delegated list:', error);
      this.delegatedList = [];
    }
  }

  showComponent(componentKey: 'leaveStart' | 'leaveEnd'): void {
    if (componentKey === 'leaveStart') {
      this.isCalenderVisible['leaveEnd'] = false;
    } else if (componentKey === 'leaveEnd') {
      this.isCalenderVisible['leaveStart'] = false;
    }
    this.isCalenderVisible[componentKey] = true;
  }
  onDateSelected(event: any, key: 'leaveStart' | 'leaveEnd'): void {

    let finalDate: Date | null = null;
  
    // ✅ Case 1: proper Date aaya
    if (event.date instanceof Date) {
      finalDate = event.date;
    }
  
    // ✅ Case 2: sirf day number aaya (like 15)
    else if (typeof event.date === 'number') {
      const baseDate =
        key === 'leaveStart'
          ? this.selectedLeaveStart
          : this.selectedLeaveEnd;
  
      if (baseDate instanceof Date) {
        finalDate = new Date(baseDate);
        finalDate.setDate(event.date); // 👈 only update day
      }
    }
  
    // ✅ fallback
    if (!finalDate) {
      finalDate =
        key === 'leaveStart'
          ? this.selectedLeaveStart
          : this.selectedLeaveEnd;
    }
  
    if (!finalDate) return;
  
    const formattedDate = this.formatDate(finalDate);
  
    if (event.type === 'leaveStart') {
      this.selectedLeaveStart = finalDate;
      const leaveStartControl = this.delegationForm.get('leaveStart');
      leaveStartControl?.setValue(formattedDate);
      leaveStartControl?.markAsDirty();
      this.isCalenderVisible[key] = false;
      return;
    }
  
    if (event.type === 'leaveEnd') {
      this.selectedLeaveEnd = finalDate;
      const leaveEndControl = this.delegationForm.get('leaveEnd');
      leaveEndControl?.setValue(formattedDate);
      leaveEndControl?.markAsDirty();
      this.isCalenderVisible[key] = false;
    }
  }

  cancelDelegation() {
    this.travelerDataLayer.reporting_manager_manager_settings_click( 0, 'Cancel' );

    if (this.initialDelegationSnapshot) {
      this.applySnapshot(this.initialDelegationSnapshot);
      this.delegatedList = [...this.initialDelegatedList];
      this.updateDelegatedSearchDisplay();
      this.delegationForm.disable();
      this.syncDelegatedFieldState();
      this.Loader.isEditableClicked = true;
    } else {
      // no prior data -> clear and go back to view mode
      this.delegationForm.reset({ onLeave: false, leaveStart: '', leaveEnd: '', delegatedTo: null });
      this.selectedLeaveStart = null;
      this.selectedLeaveEnd = null;
      this.delegatedList = [];
      this.delegatedSearchControl.setValue('');
      this.delegationForm.disable();
      this.syncDelegatedFieldState();
      this.Loader.isEditableClicked = true;
    }
    this.editSessionBaseline = null;
  }

  saveDelegation() {
    const onLeave = !!this.delegationForm.get('onLeave')?.value;
    if (onLeave && this.delegationForm.invalid) {
      this.delegationForm.get('leaveStart')?.markAsTouched();
      this.delegationForm.get('leaveEnd')?.markAsTouched();
      this.delegationForm.get('delegatedTo')?.markAsTouched();
      return;
    }

    if (this.saveButtonDisabled) {
      return;
    }
    this.travelerDataLayer.reporting_manager_manager_settings_click( 0, 'Save' );
    this.updateDelegation();
  }

  editDelegation(): void {
    this.Loader.isEditableClicked = false;
    this.delegationForm.enable();
    this.syncDelegatedFieldState();
    this.editSessionBaseline = this.captureEditBaseline();
  }

  private async loadDelegationDetails(): Promise<void> {
    this.editSessionBaseline = null;
    this.delegatedList = [];
    try {
      const user = this.getUserFromToken();
      if (!user) return;

      const res: any = await this.camService.getDelegatedUserDetails(
        user.userId,
        user.subAgentId,
        user.uid
      );

      const data = res?.data;
      if (!data) return;

      if (data?.firstName || data?.lastName || data?.emailAddress) {
        this.delegatedList = this.getUniqueDelegatedList([{
          userName: `${data?.firstName || ''} ${data?.lastName || ''}`.trim(),
          emailAddress: data?.emailAddress,
          employeeCode: data?.employeeCode,
          b2BUserId: data?.delegatedUserId
        }]);
      } else {
        this.delegatedList = [];
      }
      this.initialDelegatedList = [...this.delegatedList];

      const snapshot = {
        onLeave: !!data.onLeave,
        delegatedTo: data.delegatedUserId ?? null,
        leaveStartDate: data.leaveStartDate ?? null,
        leaveEndDate: data.leaveEndDate ?? null
      };

      this.applySnapshot(snapshot);
      this.updateDelegatedSearchDisplay();
      this.initialDelegationSnapshot = snapshot;

      this.hasDelegationData = this.commonService.hasAnyValue(this.delegationForm.value);

      // Always return to view mode after initial load
      this.Loader.isEditableClicked = true;
      this.delegationForm.disable();
      this.syncDelegatedFieldState();

    } catch (error) {
      console.error('Error loading delegation details:', error);
      // this.toastr.show('error', 'Failed to load delegation details');
    }
  }

  private async updateDelegation(): Promise<void> {
    try {
      const user = this.getUserFromToken();
      if (!user) return;

      const wasExistingDelegation = this.hasExistingDelegation(this.initialDelegationSnapshot);

      const payload = {
        B2BDelegationId: this.delegationForm.value.delegatedTo ?? null,
        OnLeave: !!this.delegationForm.value.onLeave,
        LeaveStartDate: this.toISOMidnight(this.selectedLeaveStart),
        LeaveEndDate: this.toISOMidnight(this.selectedLeaveEnd)
      };

      const res: any = await this.camService.updateUserDelegation(
        user.subAgentId,
        user.userId,
        user.uid,
        payload
      );

      if (res?.status === 'Success') {
        const successMsg = wasExistingDelegation
          ? 'Delegation updated successfully!'
          : 'Delegation added successfully!';
        this.toastr.show('success', successMsg);
        await this.loadDelegationDetails();
        this.Loader.isEditableClicked = true;
        this.delegationForm.disable();
        this.syncDelegatedFieldState();
      } else if (res?.message) {
        this.toastr.show('error', res.message);
      }
    } catch (error) {
      console.error('Error updating delegation:', error);
      this.toastr.show('error', 'Failed to update delegation');
    }
  }

  private hasExistingDelegation(snapshot: any): boolean {
    if (!snapshot) return false;
    return !!(
      snapshot?.onLeave ||
      snapshot?.delegatedTo ||
      snapshot?.leaveStartDate ||
      snapshot?.leaveEndDate
    );
  }

  private updateDelegatedSearchDisplay(): void {
    const id = this.delegationForm.get('delegatedTo')?.value;
    if (id === null || id === undefined || id === '') {
      this.delegatedSearchControl.setValue('', { emitEvent: false });
      return;
    }

    const user = this.delegatedList.find(
      (item) => String(item?.b2BUserId) === String(id)
    );
    this.delegatedSearchControl.setValue(user?.userName ?? '', { emitEvent: false });
  }

  private syncDelegatedFieldState(): void {
    if (this.Loader.isEditableClicked) {
      this.delegatedSearchControl.disable({ emitEvent: false });
      return;
    }

    this.delegatedSearchControl.enable({ emitEvent: false });
  }

  private applySnapshot(snapshot: any): void {
    this.delegationForm.patchValue({
      onLeave: !!snapshot?.onLeave,
      delegatedTo: snapshot?.delegatedTo ?? null
    });

    this.selectedLeaveStart = null;
    this.selectedLeaveEnd = null;
    this.delegationForm.get('leaveStart')?.setValue('');
    this.delegationForm.get('leaveEnd')?.setValue('');

    if (snapshot?.leaveStartDate) {
      const d = new Date(snapshot.leaveStartDate);
      if (!isNaN(d.getTime())) {
        this.selectedLeaveStart = d;
        this.delegationForm.get('leaveStart')?.setValue(this.formatDate(d));
      }
    }

    if (snapshot?.leaveEndDate) {
      const d = new Date(snapshot.leaveEndDate);
      if (!isNaN(d.getTime())) {
        this.selectedLeaveEnd = d;
        this.delegationForm.get('leaveEnd')?.setValue(this.formatDate(d));
      }
    }
  }

  private getUserFromToken() {
    const token = localStorage.getItem('QlAccessToken');
    if (!token) return null;

    const decoded: any = jwtDecode(token);

    return {
      subAgentId: decoded?.subAgentId,
      userId: sessionStorage.getItem('userId') || '',
      uid: decoded?.uid,
      role: decoded?.role
    };
  }

  private formatDate(date: Date): string {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = String(date.getFullYear());
    return `${dd}/${mm}/${yyyy}`;
  }

  private toISOMidnight(date: Date | null): string | null {
    if (!date) return null;
    const yyyy = String(date.getFullYear());
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T00:00:00`;
  }

  ngOnDestroy() {
    if (this.delegatedSearchTimer) {
      clearTimeout(this.delegatedSearchTimer);
      this.delegatedSearchTimer = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

    ManageTravelerDataData: any = null;
}
